import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFERRAL_COOKIE_NAME = 'mcs_ref';
const REFERRAL_COOKIE_DAYS = 30;

/**
 * Hook to handle referral/affiliate link tracking
 * Captures ref/aff query params and stores them in a cookie
 */
export function useReferralTracking() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Check for referral params in URL
    const refCode = searchParams.get('ref') || searchParams.get('aff');
    
    if (refCode) {
      // Store the referral code in a cookie
      setReferralCookie(refCode);
      // Keep the ref parameter visible in URL so affiliates can confirm their link works
      // The cookie is set, so tracking will continue to function
    }
  }, [searchParams]);
}

/**
 * Set a referral code cookie
 */
function setReferralCookie(code: string): void {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + REFERRAL_COOKIE_DAYS);
  
  document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(code)}; expires=${expirationDate.toUTCString()}; path=/; Secure; SameSite=Strict`;
}

/**
 * Get the stored referral code from cookie
 */
export function getReferralCode(): string | null {
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === REFERRAL_COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }
  
  return null;
}

/**
 * Clear the referral cookie (call after successful checkout)
 */
export function clearReferralCookie(): void {
  document.cookie = `${REFERRAL_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict`;
}
